import React, { useState, useMemo, useEffect } from "react";
import { format } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useDispatch, useSelector } from 'react-redux';
import { APIConstants, DashboardConstants, formatDOSDate } from '../utilities/Constants';
import { Link } from 'react-router-dom';
import {
  FileText,
  Send,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  AlertOctagon,
  Filter,
  RotateCcw,
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Calendar,
  ClipboardX,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

// Chart series identity — must match the <Bar fill> values and the legend
// swatches. The KPI tile palette is NOT here; it comes from the design
// system's tone modifiers.
const BRAND = "#1a9b8c";

const tabs = [
  { key: "counts", label: "Claim Counts" },
  { key: "clearinghouse", label: "Clearinghouse" },
  { key: "cpt", label: "CPT Claims" },
  { key: "provider", label: "Provider Claims" },
];

// ---- small building blocks --------------------------------------------

// `tone` is a design-system tone name (none / info / paid / denied / pending /
// muted). It drives BOTH the value colour and the icon tile via the generated
// .hv-stat--* modifiers — never an inline style or a page-local class.
function StatCard({ icon: Icon, label, value, delta, up, tone }) {
  return (
    <div className={"hv-stat hv-stat--" + tone}>
      <div className="hv-stat__icon">
        <Icon size={18} strokeWidth={2.25} />
      </div>
      <div className="hv-stat__body">
        <div className="hv-stat__value">{value}</div>
        <div className="hv-stat__label">{label}</div>
        <div className={"hv-stat__delta " + (up ? "hv-stat__delta--up" : "hv-stat__delta--down")}>

        </div>
      </div>
    </div>
  );
}


function TopReasonsPanel({ title, data, emptyMessage }) {
  // Display-only roll-up of the rows already rendered below — no new fetch,
  // no change to how any figure is sourced.
  const total = Array.isArray(data)
    ? data.reduce((sum, item) => sum + (Number(item.Count) || 0), 0)
    : 0;

  return (
    <section className="hv-panel hv-panel--flush">

      <div className="hv-panel__head">
        <h3 className="hv-panel__heading">{title}</h3>
        {Array.isArray(data) && data.length > 0 && (
          <span className="hv-panel__hint">{total} total</span>
        )}
      </div>

      {Array.isArray(data) && data.length > 0 ? (

        <div className="hv-table-scroll">

          <table className="hv-table hv-table--compact">

            <thead>
              <tr>
                <th>Reasons</th>
                <th className="hv-num-col">Claims</th>
              </tr>
            </thead>

            <tbody>

              {data.slice(0, 4).map((item, index) => (

                <tr key={index}>
                  <td className="hv-td-wrap">
                    {item.Reason}
                  </td>

                  <td className="hv-num-col hv-td-strong">
                    {item.Count}
                  </td>
                </tr>

              ))}

            </tbody>

          </table>
        </div>

      ) : (

        <div className="hv-panel__body">

          <div className="hv-empty hv-empty--sm">

            <div className="hv-empty__icon">
              <ClipboardX size={26} strokeWidth={1.75} />
            </div>

            <div className="hv-empty__title">
              {title === "Top Rejections"
                ? "No rejection data"
                : "No denial data"}
            </div>

            <div className="hv-empty__hint">
              {emptyMessage}
            </div>

          </div>

        </div>

      )}

    </section>
  );
}

// Count-cell class. A zero still renders — is-zero only greys it and drops
// the pointer, so the figure is never hidden.
function countLink(value) {
  return "hv-td-link" + (Number(value) === 0 ? " is-zero" : "");
}

// "All" is stored as a page size larger than any result set, so the four
// existing slice expressions and totalPages formulas keep working verbatim —
// rowsPerPage stays a number and no pagination logic is rewritten.
const ALL_ROWS = 1000000000;
const ROW_OPTIONS = [10, 20, 30, 100];

// Page numbers around the current page, with gaps once there are many.
function pageList(page, pages) {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);

  const wanted = [1, pages, page, page - 1, page + 1]
    .filter((n) => n >= 1 && n <= pages)
    .sort((a, b) => a - b);

  const out = [];
  let prev = 0;
  wanted.forEach((n) => {
    if (n === prev) return;
    if (prev && n - prev > 1) out.push("gap-" + n);
    out.push(n);
    prev = n;
  });
  return out;
}

// Row count + pagination, rendered ABOVE the table it controls.
function TableBar({ page, pages, rowsPerPage, total, unit, onPage, onRows }) {
  const size = rowsPerPage >= ALL_ROWS ? Math.max(total, 1) : rowsPerPage;
  const start = total === 0 ? 0 : (page - 1) * size + 1;
  const end = Math.min(page * size, total);

  return (
    <div className="hv-tablebar">
      <span>Showing {start}–{end} of {total} {unit}</span>

      <div className="hv-pagi">
        <span className="hv-pagi__rows">
          Rows
          <select
            className="hv-select"
            aria-label="Rows per page"
            value={rowsPerPage >= ALL_ROWS ? "all" : rowsPerPage}
            onChange={onRows}
          >
            {ROW_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
            <option value="all">All</option>
          </select>
        </span>

        <button
          className="hv-pagi__btn"
          disabled={page === 1}
          onClick={() => onPage(page - 1)}
          aria-label="Previous page"
        >
          ‹
        </button>

        {pageList(page, pages).map((n) =>
          typeof n === "number" ? (
            <button
              key={n}
              className={"hv-pagi__btn" + (n === page ? " is-active" : "")}
              onClick={() => onPage(n)}
              aria-current={n === page ? "page" : undefined}
            >
              {n}
            </button>
          ) : (
            <span key={n} className="hv-pagi__gap" aria-hidden="true">…</span>
          )
        )}

        <button
          className="hv-pagi__btn"
          disabled={pages === 0 || page === pages}
          onClick={() => onPage(page + 1)}
          aria-label="Next page"
        >
          ›
        </button>
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip-title">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="chart-tooltip-row">
          <span className="dot" style={{ background: p.fill }} />
          <span className="name">{p.name}</span>
          <span className="val">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

// ---- main component -----------------------------------------------------

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState("counts");
  const [dateType, setDateType] = useState("DOS");
  const [showPracticeClaims, setShowPracticeClaims] = useState(1);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState(''); // State to store start date
  const [endDate, setEndDate] = useState('');
  const [isStartOpen, setIsStartOpen] = useState(false);
  const [isEndOpen, setIsEndOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [denialRejectedData, setdenialRejectedData] = useState([]);
  const [providerData, setproviderData] = useState([]);
  const [cptData, setcptData] = useState([]);
  const [error, setError] = useState(null);
  const [claimCountData, setClaimCountData] = useState([]);
  const [clearingHouseData, setclearingHouseData] = useState([]);

  const [searchDOS, setSearchDOS] = useState('');
  const [searchProviderDOS, setSearchProviderDOS] = useState('');
  const [searchCptDOS, setSearchCptDOS] = useState('');
  const [searchClearingHouse, setSearchClearingHouse] = useState('');
  const [searchProvider, setSearchProvider] = useState('');
  const [searchCPT, setSearchCPT] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const totalPages = Math.ceil(claimCountData.length / rowsPerPage);

  const paginatedData = claimCountData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );




  const userData = useSelector((state) => state.user.user);
  console.log('userData:', userData);

  const dispatch = useDispatch();


  const sAuthToken = useSelector((state) => state.user.token);

  var userPracticeID = userData ? userData.practiceID : null;

  var userId = userData ? userData.userID : null;




  const handleStartDateChange = (date) => {
    if (date) {
      const formattedDate = format(date, 'MM/dd/yyyy');
      setStartDate(formattedDate);

      setIsStartOpen(false);

    } else {
      setStartDate(null);
    }
  };


  const handleEndDateChange = (date) => {

    if (date) {
      const formattedDate = format(date, 'MM/dd/yyyy');
      setEndDate(formattedDate);

      setIsEndOpen(false);

    } else {
      setEndDate(null);
    }
  };



  const getMonths = (selectedYear) => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();

    return Array.from({ length: 12 }, (v, k) => {
      if (selectedYear === currentYear && k > currentMonth) {
        return null;
      }
      return (
        <option key={k} value={k}>
          {format(new Date(selectedYear, k), 'MMMM')}
        </option>
      );
    }).filter(option => option !== null);
  };



  const handleTodayClick = (dateType) => {
    const today = new Date();

    if (dateType === "start" && startDate) {
      setStartDate(today);
      setIsStartOpen(false);
    }

    if (dateType === "end" && endDate) {
      setEndDate(today);
      setIsEndOpen(false);
    }
  };





  // Styled via .hv-cal-header / .hv-cal-nav-btn / .hv-cal-select /
  // .hv-cal-container / .hv-cal-footer / .today-btn, shared in
  // styles/healthiva/_forms.scss — same DatePicker popup treatment as
  // ClaimApprovalV2.js, PostingPaymentsV2.js, Superbillv2.js,
  // InsPaymentReport.js and LabBilling.js.
  const renderCalendarHeader = ({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => {
    const selectedDate = date instanceof Date && !isNaN(date) ? date : new Date();
    const selectedMonth = selectedDate.getMonth();
    const selectedYear = selectedDate.getFullYear();

    return (
      <div className="hv-cal-header">
        <button type="button" className="hv-cal-nav-btn" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
          {"<"}
        </button>
        <select
          className="hv-cal-select"
          value={selectedMonth}
          onChange={({ target: { value } }) => changeMonth(Number(value))}
        >
          {getMonths(selectedYear)}
        </select>
        <select
          className="hv-cal-select"
          value={selectedYear}
          onChange={({ target: { value } }) => changeYear(Number(value))}
        >
          {Array.from({ length: 100 }, (v, k) => (
            <option key={k} value={new Date().getFullYear() - k}>
              {new Date().getFullYear() - k}
            </option>
          ))}
        </select>
        <button type="button" className="hv-cal-nav-btn" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
          {">"}
        </button>
      </div>
    );
  };

  const makeCalContainer = (which) => ({ children }) => (
    <div className="hv-cal-container">
      {children}
      <div className="hv-cal-footer">
        <button type="button" className="today-btn" onClick={() => handleTodayClick(which)}>
          Today
        </button>
      </div>
    </div>
  );


  useEffect(() => {
    document.title = "Billing Dashboard";
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);

    setStartDate(sevenDaysAgo);
    setEndDate(today);

    const formattedStartDate = format(sevenDaysAgo, 'MM/dd/yyyy');
    const formattedEndDate = format(today, 'MM/dd/yyyy');
    handleAllClick(formattedStartDate, formattedEndDate);

  }, []);


  const handleGetDashboardCounts = async (startDate, endDate) => {
    setLoading(true);

    var objInput = {
      iPracticeID: userPracticeID,
      sFromDate: startDate,
      sToDate: endDate,
      sFilterText: userId,
      sFilterType: showPracticeClaims,
      sDateFilterType: dateType

    }

    var sInputJSON = JSON.stringify(objInput);


    const url = `${APIConstants.BASE_URL_BILLING}${DashboardConstants.GetDashboardCounts}?sInputJSON=${sInputJSON}`;

    console.log("URL: ", url);

    fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sAuthToken}`
      }
    })

      .then((response) => {
        if (response.status != 200) { console.log("response.status", response.status); }
        else return response.json();
      })
      .then(data => {



        const parsedData = JSON.parse(data)

        setTimeout(() => {
          setLoading(false);
          setClaimCountData(parsedData.Data);
          // setgraphData(parsedData.Data)

        }, 100);
        console.log("Response of" + DashboardConstants.GetDashboardCounts, parsedData);




      })
      .catch((error) => {
        console.error('Error fetching:', error);
      });
  }




  const handleDataClick = (startDate, endDate) => {


    var inputStruct = {

      sFromDate: startDate,
      sToDate: endDate,
      sPracticeID: userPracticeID,
      IsBiller: true,
      sUserID: userId,

    };

    var myInput = { sInputJSON: JSON.stringify(inputStruct) };
    console.log("input of" + DashboardConstants.GetDenRejReasons, myInput);
    myInput = JSON.stringify(myInput);

    fetch(APIConstants.BASE_URL + DashboardConstants.GetDenRejReasons, {
      method: 'POST',
      headers: {

        'Accept': 'application/json',
        'Content-Type': 'application/json'

      },

      body: myInput
    })

      .then(response => response.json())
      .then(data => {


        const parsedDenialRejData = JSON.parse(data.d);





        if (parsedDenialRejData.Status === 'Success') {

          const dnData = JSON.parse(parsedDenialRejData.Data.DeniedStats);
          const rejectedData = JSON.parse(parsedDenialRejData.Data.RejectedStats);

          // Set the parsed data
          setdenialRejectedData({ dnData, rejectedData });
          console.log("Response of" + DashboardConstants.GetDenRejReasons, denialRejectedData)

        } else {
          setError('API request failed');
        }
        setLoading(false);
      })
      .catch((error) => {
        setError('API request failed');
        setLoading(false);
      });

  }




  const handleGetCHSubmittedClaims = async (startDate, endDate) => {

    setLoading(true);

    var objInput = {
      PracticeID: userPracticeID,
      FromDate: startDate,
      ToDate: endDate,
      FilterText: userId,
      FilterType: showPracticeClaims,
      DateFilterType: dateType

    }

    var sInputJSON = JSON.stringify(objInput);


    const url = `${APIConstants.BASE_URL_BILLING}${DashboardConstants.GetCHSubmittedClaims}?sInputJSON=${sInputJSON}`;

    console.log("URL: ", url);

    fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sAuthToken}`

      }
    })

      .then((response) => {
        if (response.status != 200) { console.log("response.status", response.status); }
        else return response.json();
      })
      .then(data => {



        const parsedData = JSON.parse(data)

        setTimeout(() => {
          setLoading(false);
          setclearingHouseData(parsedData)
          console.log("DATA????", parsedData)

        }, 100);


      })
      .catch((error) => {
        console.error('Error fetching:', error);
      });
  }



  const handleGetProviderClaimsCount = async (startDate, endDate) => {
    setLoading(true);

    var objInput = {
      PracticeID: userPracticeID,
      FromDate: startDate,
      ToDate: endDate,
      FilterText: userId,
      FilterType: showPracticeClaims,
      DateFilterType: dateType

    }

    var sInputJSON = JSON.stringify(objInput);


    const url = `${APIConstants.BASE_URL_BILLING}${DashboardConstants.GetProviderClaimsCount}?sInputJSON=${sInputJSON}`;

    console.log("URL: ", url);

    fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sAuthToken}`
      }
    })

      .then((response) => {
        if (response.status != 200) { console.log("response.status", response.status); }
        else return response.json();
      })
      .then(data => {



        const parsedData = JSON.parse(data)

        setTimeout(() => {
          setLoading(false);
          setproviderData(parsedData)

        }, 100);


      })
      .catch((error) => {
        console.error('Error fetching:', error);
      });
  }



  const handleGetCPTClaimsCount = async (startDate, endDate) => {
    setLoading(true);

    var objInput = {
      PracticeID: userPracticeID,
      FromDate: startDate,
      ToDate: endDate,
      FilterText: userId,
      FilterType: showPracticeClaims,
      DateFilterType: dateType

    }

    var sInputJSON = JSON.stringify(objInput);


    const url = `${APIConstants.BASE_URL_BILLING}${DashboardConstants.GetCPTClaimsCount}?sInputJSON=${sInputJSON}`;

    console.log("URL: ", url);

    fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sAuthToken}`
      }
    })

      .then((response) => {
        if (response.status != 200) { console.log("response.status", response.status); }
        else return response.json();
      })
      .then(data => {



        const parsedData = JSON.parse(data)

        setTimeout(() => {
          setLoading(false);
          setcptData(parsedData)

        }, 100);


      })
      .catch((error) => {
        console.error('Error fetching:', error);
      });
  }



  const handleAllClick = async (startDate, endDate) => {
    setLoading(true);

    try {
      await Promise.all([
        handleGetDashboardCounts(startDate, endDate),
        handleDataClick(startDate, endDate),

        handleGetCHSubmittedClaims(startDate, endDate),
        handleGetProviderClaimsCount(startDate, endDate),
        handleGetCPTClaimsCount(startDate, endDate),
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {

      setTimeout(() => {
        setLoading(false);


      }, 500);
    }
  };




  const handleLinkClick = (Dos, statusType) => {
    const formattedSDate = formatDOSDate(Dos);

    const url = `/#/MedicalClaimStatus?FilterName=Status&FilterType=1&FilterDateType=DOS&FilterValue=${statusType}&FromDate=${formattedSDate}&ToDate=${formattedSDate}&FilterLabel=`

    window.open(url, '_blank');
  };


  const handleRejLinkClick = (Dos) => {
    const formattedSDate = formatDOSDate(Dos);

    const url = `/#/workableclaims/rejectedclaims?FilterName=Status&FilterType=1&FilterDateType=DOS&FromDate=${formattedSDate}&ToDate=${formattedSDate}`

    window.open(url, '_blank');
  };



  const handleDeniedClick = (Dos) => {
    const formattedSDate = formatDOSDate(Dos);

    const url = `/#/workableclaims/deniedclaims?FilterName=Status&FilterType=1&FilterDateType=DOS&FromDate=${formattedSDate}&ToDate=${formattedSDate}`

    window.open(url, '_blank');
  };


  const handleErrorClick = (Dos) => {
    const formattedSDate = formatDOSDate(Dos);

    const url = `/#/workableclaims/errorclaims?FilterName=Status&FilterType=1&FilterDateType=DOS&FromDate=${formattedSDate}&ToDate=${formattedSDate}`

    window.open(url, '_blank');
  };


  const handleFailedClick = (Dos) => {
    const formattedSDate = formatDOSDate(Dos);

    const url = `/#/workableclaims/submissionclaims?FilterName=Status&FilterType=1&FilterDateType=DOS&FromDate=${formattedSDate}&ToDate=${formattedSDate}`

    window.open(url, '_blank');
  };


  // For ClearingHouse table
  const filteredClearingHouse = clearingHouseData.filter(item => {
    const dosMatch = searchDOS === '' || (item.DOS && item.DOS.toLowerCase().includes(searchDOS.toLowerCase()));
    const clearingHouseMatch = searchClearingHouse === '' || (item.ClearingHouse && item.ClearingHouse.toLowerCase().includes(searchClearingHouse.toLowerCase()));
    return dosMatch && clearingHouseMatch;
  });

  // For CPTData table
  const filteredCPTData = cptData.filter(item => {
    const dosMatch = searchCptDOS === '' || (item.DOS && item.DOS.toLowerCase().includes(searchCptDOS.toLowerCase()));
    const cptMatch = searchCPT === '' || (item.CPTCode && item.CPTCode.toLowerCase().includes(searchCPT.toLowerCase()));
    return dosMatch && cptMatch;
  });

  // For ProviderData table
  const filteredProviderData = providerData.filter(item => {
    const dosMatch = searchProviderDOS === '' || (item.DOS && item.DOS.toLowerCase().includes(searchProviderDOS.toLowerCase()));
    const providerMatch = searchProvider === '' || (item.ProviderName && item.ProviderName.toLowerCase().includes(searchProvider.toLowerCase()));
    return dosMatch && providerMatch;
  });



  // The rows behind the currently selected tab. Feeds the KPI tiles AND the
  // Claims Overview chart — one expression, so the two can never disagree
  // about which tab is showing.
  const currentData =
    activeTab === "counts"
      ? claimCountData
      : activeTab === "clearinghouse"
        ? filteredClearingHouse
        : activeTab === "cpt"
          ? filteredCPTData
          : filteredProviderData;


  // Single column-summing helper, shared by the KPI tiles and every table's
  // <tfoot>. Previously the tiles used Number() here and the totals rows used
  // parseInt() in calculateColumnTotals — two code paths for one concept.
  const calculateColumnTotals = (data = [], keys = []) => {
    const totalsByKey = {};
    keys.forEach((key) => {
      totalsByKey[key] = data.reduce(
        (acc, item) => acc + (Number(item[key]) || 0),
        0
      );
    });
    return totalsByKey;
  };


  const totals = useMemo(() => {

    const t = calculateColumnTotals(currentData, [
      "SubmittedClaims",
      "ForwardedClaims",
      "PaidCPTs",
      "RejectedClaims",
      "DeniedCPTs",
      "ValidationErrorCPT",
      "SubmissionFailedCPT",
    ]);

    return {
      submitted: t.SubmittedClaims,
      forwarded: t.ForwardedClaims,
      paid: t.PaidCPTs,
      rejected: t.RejectedClaims,
      denied: t.DeniedCPTs,
      error: t.ValidationErrorCPT,
      failed: t.SubmissionFailedCPT
    };

  }, [currentData]);



  const summaryCards = [
    {
      key: "submitted",
      label: "Total Submitted",
      value: totals.submitted,

      up: true,
      icon: FileText,
      tone: "none"
    },
    {
      key: "forwarded",
      label: "Forwarded",
      value: totals.forwarded,

      up: true,
      icon: Send,
      tone: "info"
    },
    {
      key: "paid",
      label: "Paid",
      value: totals.paid,

      up: true,
      icon: CheckCircle2,
      tone: "paid"
    },
    {
      key: "rejected",
      label: "Rejected",
      value: totals.rejected,

      up: true,
      icon: XCircle,
      tone: "denied"
    },
    {
      key: "denied",
      label: "Denied",
      value: totals.denied,

      up: false,
      icon: AlertOctagon,
      tone: "pending"
    },
    {
      key: "failed",
      label: "Failed",
      value: totals.failed,

      up: true,
      icon: AlertTriangle,
      tone: "muted"
    }
  ];





  const summaryKeys = [
    "SubmittedClaims",
    "ForwardedClaims",
    "PaidCPTs",
    "RejectedClaims",
    "DeniedCPTs",
    "ValidationErrorCPT",
    "SubmissionFailedCPT",
  ];


  const claimCountsTotals = calculateColumnTotals(claimCountData, summaryKeys);
  const clearingHouseTotals = calculateColumnTotals(filteredClearingHouse, summaryKeys);
  const CPTCountsTotals = calculateColumnTotals(filteredCPTData, summaryKeys);
  const ProviderTotals = calculateColumnTotals(filteredProviderData, summaryKeys);



  const totalClearingHousePages = Math.ceil(filteredClearingHouse.length / rowsPerPage);

  const paginatedClearinghouseData = filteredClearingHouse.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const totalCptPages = Math.ceil(filteredCPTData.length / rowsPerPage);

  const paginatedCptData = filteredCPTData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );


  const totalProviderPages = Math.ceil(filteredProviderData.length / rowsPerPage);

  const paginatedProviderData = filteredProviderData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );


  const handleCheckboxChange = (e) => {
    setShowPracticeClaims(e.target.checked ? 0 : 1);
  };


  // Was a byte-identical copy of `currentData`. Aliased instead of duplicated
  // so the chart and the KPI tiles can never end up reading different tabs.
  const currentChartData = currentData;


  const weekdayData = useMemo(() => {

    if (!Array.isArray(currentChartData)) return [];

    const grouped = {};

    currentChartData.forEach(item => {

      const date = new Date(item.DOS);

      const day = date.toLocaleDateString("en-US", {
        weekday: "short"
      });

      if (!grouped[day]) {
        grouped[day] = {
          day,
          submitted: 0,
          paid: 0,
          denied: 0,
          rejected: 0,
          failed: 0
        };
      }

      grouped[day].submitted += Number(item.SubmittedClaims) || 0;
      grouped[day].paid += Number(item.PaidCPTs) || 0;
      grouped[day].denied += Number(item.DeniedCPTs) || 0;
      grouped[day].rejected += Number(item.RejectedClaims) || 0;
      grouped[day].failed += Number(item.SubmissionFailedCPT) || 0;
    });

    const order = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    return order
      .map(day => grouped[day] || {
        day,
        submitted: 0,
        paid: 0,
        denied: 0,
        rejected: 0,
        failed: 0
      });

  }, [currentChartData]);
  return (
    <div className="hv-page">

      <div className="hv-container">
        {/* Header */}
        <header className="hv-pagehead">
          <div>
            <h1 className="hv-pagehead__title">Claims Dashboard</h1>
            <div className="hv-pagehead__sub">Monitor claim activity and clearinghouse performance</div>
          </div>

          <div className="hv-pagehead__actions">
            {/* TODO: wire to a CSV download of the active tab's rows. Left
                without an onClick deliberately — no export handler exists on
                this page yet and none was invented. */}
            <button className="hv-btn hv-btn--ghost">
              <Download size={14} /> Export
            </button>
          </div>
        </header>

        {/* Filter bar */}
        <section className="hv-toolbar" aria-label="Filters">
          <div className="hv-toolbar__group">

            <select className="hv-select" value={dateType} onChange={(e) => setDateType(e.target.value)}>
              <option value="DOS" >DOS</option>
              <option value="DOA" >To Approve</option>
              <option value="DOSub" >Submit Date</option>
            </select>
          </div>
          {/* From/To stay in one group so the pair never breaks across a wrap */}
          <div className="hv-toolbar__group">

            <label className="hv-field__label">From Date:</label>
            <DatePicker
              selected={startDate ? new Date(startDate) : null}
              onChange={handleStartDateChange}
              dateFormat="MM/dd/yyyy"
              maxDate={new Date()}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              yearDropdownItemNumber={100}

              className={`hv-date clnImgTextBoxes ${startDate ? 'default-selected' : ''}`}
              placeholderText="MM/dd/yyyy"
              calendarContainer={makeCalContainer("start")}
              open={isStartOpen}
              onFocus={() => setIsStartOpen(true)}
              onInputClick={() => setIsStartOpen(true)}
              onClickOutside={() => setIsStartOpen(false)}
              renderCustomHeader={renderCalendarHeader}
            />

            <label className="hv-field__label">To Date:</label>
            <DatePicker
              selected={endDate ? new Date(endDate) : null}
              onChange={handleEndDateChange}
              dateFormat="MM/dd/yyyy"
              maxDate={new Date()}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              yearDropdownItemNumber={100}
              className={`hv-date clnImgTextBoxes ${endDate ? 'default-selected' : ''}`}
              placeholderText="MM/dd/yyyy"
              popperPlacement="bottom-start"
              calendarContainer={makeCalContainer("end")}
              open={isEndOpen}
              onFocus={() => setIsEndOpen(true)}
              onInputClick={() => setIsEndOpen(true)}
              onClickOutside={() => setIsEndOpen(false)}
              renderCustomHeader={renderCalendarHeader}
            />
          </div>

          <label className="hv-check">
            <input
              type="checkbox"
              checked={showPracticeClaims == 0}
              onChange={handleCheckboxChange}

            />
             <span> Show Practice Claims</span>
          </label>
          <button className="hv-btn hv-btn--primary" onClick={() => handleAllClick(startDate, endDate)}>
            <Filter size={14} /> Apply Filters
          </button>

          <div className="hv-spacer" />

          {/* .hv-search draws its own magnifier via ::before — no icon element.
              Bound to the existing `query` state; no new state invented.
              TODO: this does not filter yet. The page's real search is
              per-column (searchDOS / searchClearingHouse / searchCPT /
              searchProvider); a global filter across all four datasets is a
              separate logic change. */}
          <div className="hv-search">
            <input
              type="search"
              placeholder="Search claim, patient, payer..."
              aria-label="Search claims"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

        </section>

        {/* Stat cards */}
        <div className="hv-grid">
          {summaryCards.map((c) => (
            <StatCard key={c.key} {...c} />
          ))}
        </div>

        {/* Tabs */}
        <nav className="hv-tabbar" role="tablist" aria-label="Claim views">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={activeTab === t.key}
              className={"hv-tabbar__tab" + (activeTab === t.key ? " is-active" : "")}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {/* Table */}

        {activeTab === "counts" && (
          <section className="hv-panel hv-panel--flush">
            <div className="hv-panel__head">
              <h3 className="hv-panel__heading">Claim Counts Summary</h3>
              <span className="hv-panel__hint">Click any number to open that claim list</span>
              <div className="hv-panel__actions">

               
              </div>

            <TableBar
              page={currentPage}
              pages={totalPages}
              rowsPerPage={rowsPerPage}
              total={claimCountData.length}
              unit="days"
              onPage={(n) => setCurrentPage(n)}
              onRows={(e) => {
                const v = e.target.value;
                setRowsPerPage(v === "all" ? ALL_ROWS : Number(v));
                setCurrentPage(1);
              }}
            />
            </div>



            <div className="hv-table-scroll"><table className="hv-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>DOS</th>
                  <th className="hv-num-col">Submitted</th>
                  <th className="hv-num-col">Forwarded</th>
                  <th className="hv-num-col">Paid</th>
                  <th className="hv-num-col">Rejected</th>
                  <th className="hv-num-col">Denied</th>
                  <th className="hv-num-col">Error</th>
                  <th className="hv-num-col">Failed</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((item, index) => (

                    <tr key={index}>
                      <td><span className="hv-td-muted">{(currentPage - 1) * rowsPerPage + index + 1}</span></td>
                      <td><span className="hv-td-strong">{formatDOSDate(item.DOS)}</span></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.SubmittedClaims)} onClick={() => handleLinkClick(item.DOS, 'sub')}>{item.SubmittedClaims}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.ForwardedClaims)} onClick={() => handleLinkClick(item.DOS, '5')}>{item.ForwardedClaims}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.PaidCPTs)} onClick={() => handleLinkClick(item.DOS, '1')}>{item.PaidCPTs}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.RejectedClaims)} onClick={() => handleRejLinkClick(item.DOS)}>{item.RejectedClaims}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.DeniedCPTs)} onClick={() => handleDeniedClick(item.DOS)}>{item.DeniedCPTs}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.ValidationErrorCPT)} onClick={() => handleErrorClick(item.DOS)}>{item.ValidationErrorCPT}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.SubmissionFailedCPT)} onClick={() => handleFailedClick(item.DOS)}>{item.SubmissionFailedCPT}</button></td>

                    </tr>

                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="hv-t-center hv-td-muted">
                      No Records Found
                    </td>
                  </tr>
                )}
              </tbody>

              {/* Exactly nine cells, no colspan, so the totals line up with
                  the body columns above. Same expressions as before. */}
              <tfoot>
                <tr className="hv-tr-total">
                  <td />
                  <td>Total</td>
                  {summaryKeys.map((key) => (
                    <td key={key} className="hv-num-col">
                      {claimCountsTotals[key] || 0}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table></div>
          </section>

        )}

        {activeTab === "clearinghouse" && (
          <section className="hv-panel hv-panel--flush">
            <div className="hv-panel__head">
              <h3 className="hv-panel__heading">ClearingHouse Summary</h3>
              <span className="hv-panel__hint">Click any number to open that claim list</span>
              <div className="hv-panel__actions">

               
              </div>

            <TableBar
              page={currentPage}
              pages={totalClearingHousePages}
              rowsPerPage={rowsPerPage}
              total={filteredClearingHouse.length}
              unit="rows"
              onPage={(n) => setCurrentPage(n)}
              onRows={(e) => {
                const v = e.target.value;
                setRowsPerPage(v === "all" ? ALL_ROWS : Number(v));
                setCurrentPage(1);
              }}
            />
            </div>



            <div className="hv-table-scroll"><table className="hv-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>DOS</th>
                  <th>Clearing House</th>
                  <th className="hv-num-col">Submitted</th>
                  <th className="hv-num-col">Forwarded</th>
                  <th className="hv-num-col">Paid</th>
                  <th className="hv-num-col">Rejected</th>
                  <th className="hv-num-col">Denied</th>
                  <th className="hv-num-col">Error</th>
                  <th className="hv-num-col">Failed</th>
                </tr>

                {/* Same state and handlers as before — only the markup moved
                    out of the header cells into their own sticky row. */}
                <tr className="hv-table__searchrow">
                  <th />
                  <th>
                    <input
                      type="text"
                      placeholder="Search"
                      aria-label="Search DOS"
                      value={searchDOS}
                      onChange={(e) => setSearchDOS(e.target.value)}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      placeholder="Search"
                      aria-label="Search Clearing House"
                      value={searchClearingHouse}
                      onChange={(e) => setSearchClearingHouse(e.target.value)}
                    />
                  </th>
                  <th /><th /><th /><th /><th /><th /><th />
                </tr>
              </thead>
              <tbody>

                {paginatedClearinghouseData.length > 0 ? (
                  paginatedClearinghouseData.map((item, index) => (

                    <tr key={index}>
                      <td><span className="hv-td-muted">{(currentPage - 1) * rowsPerPage + index + 1}</span></td>
                      <td><span className="hv-td-strong">{formatDOSDate(item.DOS)}</span></td>
                      <td>{item.ClearingHouse}</td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.SubmittedClaims)} onClick={() => handleLinkClick(item.DOS, 'sub')}>{item.SubmittedClaims}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.ForwardedClaims)} onClick={() => handleLinkClick(item.DOS, '5')}>{item.ForwardedClaims}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.PaidCPTs)} onClick={() => handleLinkClick(item.DOS, '1')}>{item.PaidCPTs}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.RejectedClaims)} onClick={() => handleRejLinkClick(item.DOS)}>{item.RejectedClaims}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.DeniedCPTs)} onClick={() => handleDeniedClick(item.DOS)}>{item.DeniedCPTs}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.ValidationErrorCPT)} onClick={() => handleErrorClick(item.DOS)}>{item.ValidationErrorCPT}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.SubmissionFailedCPT)} onClick={() => handleFailedClick(item.DOS)}>{item.SubmissionFailedCPT}</button></td>

                    </tr>

                  ))
                ) : (
                  <tr>
                    <td colSpan={10} className="hv-t-center hv-td-muted">
                      No Records Found
                    </td>
                  </tr>
                )}
              </tbody>

              <tfoot>
                <tr className="hv-tr-total">
                  <td />
                  <td>Total</td>
                  <td />
                  {summaryKeys.map((key) => (
                    <td key={key} className="hv-num-col">
                      {clearingHouseTotals[key] || 0}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table></div>
          </section>

        )}


        {activeTab === "cpt" && (
          <section className="hv-panel hv-panel--flush">
            <div className="hv-panel__head">
              <h3 className="hv-panel__heading">Cpt Claim Summary</h3>
              <span className="hv-panel__hint">Click any number to open that claim list</span>
              <div className="hv-panel__actions">

               
              </div>

            <TableBar
              page={currentPage}
              pages={totalCptPages}
              rowsPerPage={rowsPerPage}
              total={filteredCPTData.length}
              unit="rows"
              onPage={(n) => setCurrentPage(n)}
              onRows={(e) => {
                const v = e.target.value;
                setRowsPerPage(v === "all" ? ALL_ROWS : Number(v));
                setCurrentPage(1);
              }}
            />
            </div>



            <div className="hv-table-scroll"><table className="hv-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>DOS</th>
                  <th>CPT</th>
                  <th className="hv-num-col">Submitted</th>
                  <th className="hv-num-col">Forwarded</th>
                  <th className="hv-num-col">Paid</th>
                  <th className="hv-num-col">Rejected</th>
                  <th className="hv-num-col">Denied</th>
                  <th className="hv-num-col">Error</th>
                  <th className="hv-num-col">Failed</th>
                </tr>

                <tr className="hv-table__searchrow">
                  <th />
                  <th>
                    <input
                      type="text"
                      placeholder="Search"
                      aria-label="Search DOS"
                      value={searchCptDOS}
                      onChange={(e) => setSearchCptDOS(e.target.value)}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      placeholder="Search"
                      aria-label="Search CPT"
                      value={searchCPT}
                      onChange={(e) => setSearchCPT(e.target.value)}
                    />
                  </th>
                  <th /><th /><th /><th /><th /><th /><th />
                </tr>
              </thead>
              <tbody>

                {paginatedCptData.length > 0 ? (
                  paginatedCptData.map((item, index) => (

                    <tr key={index}>
                      <td><span className="hv-td-muted">{(currentPage - 1) * rowsPerPage + index + 1}</span></td>
                      <td><span className="hv-td-strong">{formatDOSDate(item.DOS)}</span></td>
                      <td>{item.CPTCode}</td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.SubmittedClaims)} onClick={() => handleLinkClick(item.DOS, 'sub')}>{item.SubmittedClaims}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.ForwardedClaims)} onClick={() => handleLinkClick(item.DOS, '5')}>{item.ForwardedClaims}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.PaidCPTs)} onClick={() => handleLinkClick(item.DOS, '1')}>{item.PaidCPTs}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.RejectedClaims)} onClick={() => handleRejLinkClick(item.DOS)}>{item.RejectedClaims}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.DeniedCPTs)} onClick={() => handleDeniedClick(item.DOS)}>{item.DeniedCPTs}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.ValidationErrorCPT)} onClick={() => handleErrorClick(item.DOS)}>{item.ValidationErrorCPT}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.SubmissionFailedCPT)} onClick={() => handleFailedClick(item.DOS)}>{item.SubmissionFailedCPT}</button></td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} className="hv-t-center hv-td-muted">
                      No Records Found
                    </td>
                  </tr>
                )}
              </tbody>

              <tfoot>
                <tr className="hv-tr-total">
                  <td />
                  <td>Total</td>
                  <td />
                  {summaryKeys.map((key) => (
                    <td key={key} className="hv-num-col">
                      {CPTCountsTotals[key] || 0}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table></div>
          </section>

        )}


        {activeTab === "provider" && (
          <section className="hv-panel hv-panel--flush">
            <div className="hv-panel__head">
              <h3 className="hv-panel__heading">Provider Claim Summary</h3>
              <span className="hv-panel__hint">Click any number to open that claim list</span>
              <div className="hv-panel__actions">

               
              </div>

            <TableBar
              page={currentPage}
              pages={totalProviderPages}
              rowsPerPage={rowsPerPage}
              total={filteredProviderData.length}
              unit="rows"
              onPage={(n) => setCurrentPage(n)}
              onRows={(e) => {
                const v = e.target.value;
                setRowsPerPage(v === "all" ? ALL_ROWS : Number(v));
                setCurrentPage(1);
              }}
            />
            </div>



            <div className="hv-table-scroll"><table className="hv-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>DOS</th>
                  <th>Provider Name</th>
                  <th className="hv-num-col">Submitted</th>
                  <th className="hv-num-col">Forwarded</th>
                  <th className="hv-num-col">Paid</th>
                  <th className="hv-num-col">Rejected</th>
                  <th className="hv-num-col">Denied</th>
                  <th className="hv-num-col">Error</th>
                  <th className="hv-num-col">Failed</th>
                </tr>

                <tr className="hv-table__searchrow">
                  <th />
                  <th>
                    <input
                      type="text"
                      placeholder="Search"
                      aria-label="Search DOS"
                      value={searchProviderDOS}
                      onChange={(e) => setSearchProviderDOS(e.target.value)}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      placeholder="Search"
                      aria-label="Search Provider Name"
                      value={searchProvider}
                      onChange={(e) => setSearchProvider(e.target.value)}
                    />
                  </th>
                  <th /><th /><th /><th /><th /><th /><th />
                </tr>
              </thead>
              <tbody>


                {paginatedProviderData.length > 0 ? (
                  paginatedProviderData.map((item, index) => (

                    <tr key={index}>
                      <td><span className="hv-td-muted">{(currentPage - 1) * rowsPerPage + index + 1}</span></td>
                      <td><span className="hv-td-strong">{formatDOSDate(item.DOS)}</span></td>
                      <td>{item.ProviderName}</td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.SubmittedClaims)} onClick={() => handleLinkClick(item.DOS, 'sub')}>{item.SubmittedClaims}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.ForwardedClaims)} onClick={() => handleLinkClick(item.DOS, '5')}>{item.ForwardedClaims}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.PaidCPTs)} onClick={() => handleLinkClick(item.DOS, '1')}>{item.PaidCPTs}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.RejectedClaims)} onClick={() => handleRejLinkClick(item.DOS)}>{item.RejectedClaims}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.DeniedCPTs)} onClick={() => handleDeniedClick(item.DOS)}>{item.DeniedCPTs}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.ValidationErrorCPT)} onClick={() => handleErrorClick(item.DOS)}>{item.ValidationErrorCPT}</button></td>
                      <td className="hv-num-col"><button type="button" className={countLink(item.SubmissionFailedCPT)} onClick={() => handleFailedClick(item.DOS)}>{item.SubmissionFailedCPT}</button></td>

                    </tr>

                  ))
                ) : (
                  <tr>
                    <td colSpan={10} className="hv-t-center hv-td-muted">
                      No Records Found
                    </td>
                  </tr>
                )}

              </tbody>

              <tfoot>
                <tr className="hv-tr-total">
                  <td />
                  <td>Total</td>
                  <td />
                  {summaryKeys.map((key) => (
                    <td key={key} className="hv-num-col">
                      {ProviderTotals[key] || 0}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table></div>
          </section>

        )}

        {/* Bottom row: chart + two empty panels */}
        <div className="hv-analytics">
          <section className="hv-panel hv-analytics__hero">
            <div className="hv-panel__head">
              <h3 className="hv-panel__heading">Claims Overview</h3>
              <span className="hv-panel__hint">This week</span>
            </div>
            <div className="hv-panel__body">
            {/* .hv-chart gives ResponsiveContainer a parent with a definite
                width (100%) and height (clamp 180-260px) to measure against.
                Recharts sizes itself from the parent box, so height="100%"
                here rather than a fixed pixel value. */}
            <div className="hv-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weekdayData} barGap={2} barCategoryGap="28%">
                <CartesianGrid vertical={false} stroke="#f0f2f4" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#8a94a6" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#8a94a6" }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(26,155,140,0.06)" }} />
                <Bar dataKey="submitted" name="Submitted" fill={BRAND} radius={[3, 3, 0, 0]} />
                <Bar dataKey="paid" name="Paid" fill="#34d399" radius={[3, 3, 0, 0]} />
                <Bar dataKey="denied" name="Denied" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                <Bar dataKey="rejected" name="Rejected" fill="#ef4444" radius={[3, 3, 0, 0]} />
                <Bar dataKey="failed" name="Failed" fill="#6b7280" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            </div>
            {/* Swatch colours are chart series identities and must match the
                <Bar fill> values above, so they stay inline per the design
                system's data-driven-colour exception. */}
            <div className="hv-legend">
              <span className="hv-legend__item"><span className="hv-legend__dot" style={{ background: BRAND }} />Submitted</span>
              <span className="hv-legend__item"><span className="hv-legend__dot" style={{ background: "#34d399" }} />Paid</span>
              <span className="hv-legend__item"><span className="hv-legend__dot" style={{ background: "#f59e0b" }} />Denied</span>
              <span className="hv-legend__item"><span className="hv-legend__dot" style={{ background: "#ef4444" }} />Rejected</span>
              <span className="hv-legend__item"><span className="hv-legend__dot" style={{ background: "#6b7280" }} />Failed</span>
            </div>
            </div>
          </section>

          <TopReasonsPanel
            title="Top Rejections"
            data={denialRejectedData?.rejectedData}
            emptyMessage="There is no rejection data for the selected date range."
          />

          <TopReasonsPanel
            title="Top Denials"
            data={denialRejectedData?.dnData}
            emptyMessage="There is no rejection data for the selected date range."
          />

        </div>
      </div>
    </div>
  );
}